import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import {
  Payrun,
  PayrunsResponse,
  CreatePayrunResponse,
  PayrunStatus,
} from "@/types/payrun";
import { useToast } from "@/components/ui/use-toast";
import MenuBar from "@/components/MenuBar";
import { ScrollArea } from "@/components/ui/scroll-area";

interface PayrunOverviewProps {
  shouldHaveMenuBar?: boolean;
  selectedUserId?: string;
}

// Helper function to map status to badge variant
const getStatusBadgeVariant = (
  status: PayrunStatus,
): "default" | "secondary" | "outline" | "destructive" => {
  switch (status) {
    case "Draft":
      return "secondary"; // Use Salmon for Draft
    case "Submitted":
      return "outline";   // Use Outline for Submitted
    case "Approved":
    case "Paid":
      return "default";   // Use Primary (dark gray) for Approved/Paid
    case "Invalid":
      return "destructive"; // Use Red for Invalid
    default:
      return "outline";
  }
};

const PayrunOverview: React.FC<PayrunOverviewProps> = ({
  shouldHaveMenuBar,
  selectedUserId = "current",
}) => {
  const [payruns, setPayruns] = useState<Payrun[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    fetchPayruns();
  }, [selectedUserId]);

  const fetchPayruns = async () => {
    try {
      setIsLoading(true);

      const queryParams: Record<string, string | number> = {};
      if (selectedUserId !== "current") {
        queryParams.userId = selectedUserId;
      }

      const data = await ApiService.request<PayrunsResponse>(
        EndpointKeys.GET_PAYRUNS,
        { queryParams },
      );
      setPayruns(data.payruns);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch payruns. Please try again.",
      });
      console.error("Error fetching payruns:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreatePayrun = async () => {
    try {
      setIsLoading(true);

      const queryParams: Record<string, string | number> = {};
      if (selectedUserId !== "current") {
        queryParams.userId = selectedUserId;
      }

      const response = await ApiService.request<CreatePayrunResponse>(
        EndpointKeys.CREATE_PAYRUN,
        {
          method: "POST",
          queryParams,
        },
      );

      if (response.success) {
        toast({
          title: "Success",
          description: "Payrun created successfully",
        });
        await fetchPayruns();
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to create payrun. Please try again.",
      });
      console.error("Error creating payrun:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRunPayroll = (payrunId: number, status: PayrunStatus) => {
    // Include the userId in the navigation if it's not "current"
    const queryParam =
      selectedUserId !== "current" ? `?userId=${selectedUserId}` : "";

    if (status === "Draft") {
      navigate(`/run-payroll/${payrunId}${queryParam}`);
      return;
    }
    navigate(`/payroll-approval/${payrunId}${queryParam}`);
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toISOString().split("T")[0];
  };

  return (
    <div className={shouldHaveMenuBar ? "flex min-h-screen" : ""}>
      {shouldHaveMenuBar ? (
        <div className="w-64 flex-none">
          <MenuBar />
        </div>
      ) : null}
      <main
        id="payrun-overview"
        className={shouldHaveMenuBar ? "flex-1 p-8" : "flex-1"}
      >
        <Card>
          <CardHeader>
            <CardTitle className="font-archivo">Payrun Overview</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center items-center py-8">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : (
              <ScrollArea className="w-full">
                {payruns.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Pay Period Start</TableHead>
                          <TableHead>Pay Period End</TableHead>
                          <TableHead>Pay Date</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {payruns
                          .sort(
                            (a, b) =>
                              new Date(b.payDate).getTime() -
                              new Date(a.payDate).getTime(),
                          )
                          .map((payrun) => (
                            <TableRow key={payrun.id}>
                              <TableCell className="whitespace-nowrap">
                                {formatDate(payrun.payPeriodStartDate)}
                              </TableCell>
                              <TableCell className="whitespace-nowrap">
                                {formatDate(payrun.payPeriodEndDate)}
                              </TableCell>
                              <TableCell className="whitespace-nowrap">
                                {formatDate(payrun.payDate)}
                              </TableCell>
                              <TableCell>
                                <Badge
                                  variant={getStatusBadgeVariant(payrun.status)}
                                >
                                  {payrun.status}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  onClick={() =>
                                    handleRunPayroll(payrun.id, payrun.status)
                                  }
                                >
                                  {payrun.status === "Draft"
                                    ? "Run Payroll"
                                    : "View Payroll"}
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-sm text-muted-foreground mb-4">
                      No payruns available.
                    </p>
                    <Button onClick={handleCreatePayrun} disabled={isLoading}>
                      Create Payrun
                    </Button>
                  </div>
                )}
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default PayrunOverview;
